import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Modal, message } from 'ant-design-vue'
import { useStore } from '@/store'
import { getProductList, passProduct, rejectProduct, cancelSaleArea, deletedProducts } from '@/api/goodsJudge'
import { handleExportFile, OK, reportEvent } from '@/common'
import { exportGoodsCsv } from '@/api/goodsSetting'
import type { Key } from 'ant-design-vue/es/table/interface'
import type { ColumnType } from 'ant-design-vue/es/table'
import dayjs from 'dayjs'
import { debounce } from 'lodash'

// 审核状态常量
const STATUS_DELETED = 100 // 已删除状态

interface EmitsType {
  (e: 'goDetail', id: string | number, skuId: string | number): void
  (e: 'update:tableLoading', value: boolean): void
  (e: 'updateStatistics'): void
}

interface filterObj {
  keyword?: string | number
  publishStatuses?: number[]
  prodChnl?: string
  [key: string]: any
}

// 表格列定义
export const columns: ColumnType[] = [
  { title: '序号', dataIndex: 'index', key: 'index', width: 60 },
  { title: 'id', dataIndex: 'id', key: 'id', width: 120 },
  { title: '商品类型', dataIndex: 'prodType', key: 'prodType', width: 120 },
  { title: '内购ID', dataIndex: 'prodId', key: 'prodId', width: 300 },
  { title: '商品名称', dataIndex: 'name', key: 'name', width: 120 },
  { title: '商品描述', dataIndex: 'prodDescription', key: 'prodDescription', width: 200 },
  { title: '默认价格', dataIndex: 'price', key: 'price' },
  { title: '审核状态', dataIndex: 'approvalStatus', key: 'approvalStatus', width: 120 },
  { title: '同步状态', dataIndex: 'publishStatus', key: 'publishStatus', width: 120 },
  { title: '商品状态', dataIndex: 'prodStatus', key: 'prodStatus', width: 120 },
  { title: '审核类型', dataIndex: 'optType', key: 'optType' },
  { title: '提交人', dataIndex: 'submitName', key: 'submitName' },
  { title: '最近提审时间', dataIndex: 'submitTime', key: 'submitTime', width: '160px' },
  { title: '最近同步时间', dataIndex: 'publishTime', key: 'publishTime', width: '160px' },
  { title: '操作', dataIndex: 'handle', key: 'handle', align: 'right', fixed: 'right', width: 180 },
]

// 状态列表
export const publishStatusList: { [key: number]: string } = { 0: '--', 10: '成功', 20: '失败' }
export const approvalStatusList: { [key: number]: string } = { 0: '待审核', 10: '审核中', 20: '已驳回', 30: '已通过' }
export const prodStatusList: { [key: number]: string } = { 0: '--', 10: '有效', 20: '失效' }
export const statusColorList: { [key: number]: string } = { 0: 'orange', 10: 'blue', 20: 'red', 30: 'green' }
export const optTypeList: { [key: number]: string } = { 0: '新增', 10: '修改', 20: '停用', 30: '启用' }
export const prodTypeList: { [key: number]: string } = {
  0: '未知',
  10: '非消耗型',
  20: '消耗型',
  31: '非自动续期订阅',
  32: '自动续期订阅',
}

export const useJudgeTable = (emits: EmitsType) => {
  const userInfo = computed(() => useStore().userInfo && JSON.parse(useStore().userInfo))
  const dataSource = ref<any[]>([])
  const selectedRowKeys = ref<Key[]>([])
  const pageIndex = ref<number>(1)
  const pageSize = ref<number>(20)
  const pageTotal = ref<number>(0)
  const searchObj = ref<filterObj>({})
  const tableRef = ref<any>()
  const tableHeight = ref<number>(500)
  const editItem = ref<any>({})
  const loading = ref(false)
  const passLoading = ref(false)
  const rejectVisible = ref(false)
  const isEditModal = ref(true)
  const appName = ref('')
  const exportLoading = ref(false)
  const canDelList = ref<any[]>([])
  // 同步商品数据留推送文案
  const syncProductSSEMessage = ref<string[]>([])
  // 推送loading状态
  const sseMessageLoading = ref(false)
  const sseMessageModal = ref(false)
  const delModalVisible = ref(false)

  const getData = async () => {
    const startTime = new Date().getTime()
    let params: any = {
      pageNum: 1,
      pageSize: 100000,
      // approvalStatuses: [10, 20, 30],
      fuzzy: searchObj.value?.productName,
      orderBy: 'submit_time', // 支持的排序字段,字段后端服务要校验，审核信息页传：publish_time,商品审核页传：submit_time
      orderDirection: 'desc', // 排序方式
    }
    Object.keys(searchObj.value).forEach((item) => {
      if (!['timeRangeType', 'timeRangeValue'].includes(item)) {
        if (searchObj.value[item] || searchObj.value[item] === 0) {
          params = {
            ...params,
            [item]: searchObj.value[item],
          }
        }
      }
    })
    // 时间单独处理
    if (searchObj.value.timeRangeValue) {
      if (searchObj.value.timeRangeType === 'judge') {
        params = {
          // 提审时间
          ...params,
          submitTimeStart: dayjs(searchObj.value.timeRangeValue[0]).format('YYYY-MM-DD HH:mm:ss'),
          submitTimeEnd: dayjs(searchObj.value.timeRangeValue[1]).format('YYYY-MM-DD HH:mm:ss'),
        }
      } else {
        // 同步时间
        params = {
          ...params,
          publishTimeStart: dayjs(searchObj.value.timeRangeValue[0]).format('YYYY-MM-DD HH:mm:ss'),
          publishTimeEnd: dayjs(searchObj.value.timeRangeValue[1]).format('YYYY-MM-DD HH:mm:ss'),
        }
      }
    }
    if (searchObj.value.approvalStatuses.includes(STATUS_DELETED)) {
      params.isDeleted = 1
      delete params.approvalStatuses
    }
    dataSource.value = []
    emits('update:tableLoading', true)
    canDelList.value = []
    try {
      const { data, code } = await getProductList(params)
      if (code === OK) {
        if (data) {
          pageTotal.value = data.total
          dataSource.value = data.list.map((item: any) => {
            return {
              ...item,
              changePriceList: item.productNativePrice?.filter(
                (priceInfo: any) => priceInfo.originProdPrice !== priceInfo.prodPrice,
              ),
            }
          })
        } else {
          pageTotal.value = 0
        }
        reportEvent({
          eventName: '_search',
          properties: {
            ui_name: '商品审核搜索',
            app_name: '',
            return_duration: (new Date().getTime() - startTime) / 1000,
            query_status: 'succeed',
          },
        })
      } else {
        reportEvent({
          eventName: '_search',
          properties: {
            ui_name: '商品审核搜索',
            app_name: '',
            return_duration: (new Date().getTime() - startTime) / 1000,
            query_status: 'failed',
          },
        })
      }
    } catch (e) {
      reportEvent({
        eventName: '_search',
        properties: {
          ui_name: '商品审核搜索',
          app_name: '',
          return_duration: (new Date().getTime() - startTime) / 1000,
          query_status: 'failed',
        },
      })
      console.log('%c TableBlock.vue-145', 'color:#fff;background:#e4393c;padding:2px', e)
    }
    emits('update:tableLoading', false)
  }

  const searchData = async (obj: filterObj) => {
    selectedRowKeys.value = []
    pageIndex.value = 1
    searchObj.value = obj
    await getData()
  }

  const openDetail = async (item: any) => {
    editItem.value = item
    emits('goDetail', item.id, item.skuId)
  }

  const batchPass = async () => {
    if (!selectedRowKeys.value.length) return message.error('请选择商品')
    const productList = selectedRowKeys.value?.map((item: any) => {
      return {
        id: item,
      }
    })
    passLoading.value = true
    try {
      const { code, msg } = await passProduct({
        productList,
        prodChnl: searchObj.value.prodChnl || '',
      })
      if (code === OK) {
        Modal.confirm({
          title: '商品已通过审核！',
          centered: true,
          content: '商品已通过审核，是否立即同步到第三方后台？',
          okText: '立即同步',
          cancelText: '稍后',
          onOk: () => {
            batchPublishProduct()
            passLoading.value = false
          },
          onCancel: async () => {
            selectedRowKeys.value = []
            message.success(msg)
            pageIndex.value = 1
            await getData()
            emits('updateStatistics')
            passLoading.value = false
          },
        })
      } else {
        passLoading.value = false
      }
    } catch (e) {
      passLoading.value = false
      console.log('%c[JudgeTableBlock.vue] - 188', 'padding:2px;border-radius:4px;background:lightcoral;color:#fff', e)
    }
  }

  const batchReject = async () => {
    isEditModal.value = false
    rejectVisible.value = true
  }

  // 防抖处理可删除列表的计算
  const updateCanDelList = debounce((selectedRows: any[]) => {
    // 根据当前勾选项过滤出所有符合删除商品条件的数据的合集
    canDelList.value = selectedRows.filter((item) => item.prodStatus == 20 && item.publishStatus == 10)
  }, 300)

  const onSelectChange = (keys: Key[], selectedRows: any[]) => {
    selectedRowKeys.value = keys
    updateCanDelList(selectedRows)
  }

  const batchPublishProduct = async () => {
    if (!selectedRowKeys.value.length) return message.error('请选择商品')
    // const productList = selectedRowKeys.value?.map((item: any) => {
    //   return {
    //     id: item,
    //   }
    // })
    loading.value = true
    try {
      // const {code, msg} = await publishProduct({
      //   productList,
      //   prodChnl: searchObj.value.prodChnl || '',
      // })

      // if (code === OK) {
      //   message.success(msg)
      // }
      // selectedRowKeys.value = []
      // pageIndex.value = 1
      // await getData()
      // emits('updateStatistics')
      // loading.value = false
      // reportEvent({
      //   eventName: 'element_use',
      //   properties: {
      //     ui_name: '商品审核',
      //     element_name: '同步商品',
      //   },
      // })
      syncProductSSE()
    } catch (e) {
      loading.value = false
      console.log('%c[JudgeTableBlock.vue] - 188', 'padding:2px;border-radius:4px;background:lightcoral;color:#fff', e)
    }
  }

  const openRejectModal = (item: any) => {
    isEditModal.value = false
    editItem.value = item
    selectedRowKeys.value = [item.id]
    rejectVisible.value = true
  }

  const productPass = async (record: any) => {
    try {
      const { code, msg } = await passProduct({
        productList: [{ id: record.id }],
        prodChnl: searchObj.value.prodChnl || '',
      })
      if (code === OK) {
        message.success(msg)
        pageIndex.value = 1
        await getData()
        emits('updateStatistics')
        reportEvent({
          eventName: 'element_use',
          properties: {
            ui_name: '商品审核',
            element_name: '通过',
          },
        })
      }
    } catch (e) {
      console.log('%c[JudgeTableBlock.vue] - 333', 'padding:2px;border-radius:4px;background:lightcoral;color:#fff', e)
    }
  }

  const submitReject = async (reason: string) => {
    try {
      const { code, msg } = await rejectProduct({
        productList: dataSource.value
          .filter(
            (item: any) =>
              selectedRowKeys.value.includes(item.id) && !(item.approvalStatus === 20 || !!item.prodStatus),
          )
          .map((item: any) => ({ id: item.id })),
        rejectMsg: reason,
        prodChnl: searchObj.value.prodChnl || '',
      })
      if (code === OK) {
        message.success(msg)
        pageIndex.value = 1
        selectedRowKeys.value = []
        await getData()
        emits('updateStatistics')
        rejectVisible.value = false
        reportEvent({
          eventName: 'element_use',
          properties: {
            ui_name: '商品审核',
            element_name: '驳回',
          },
        })
      }
    } catch (e) {
      console.log('%c[JudgeTableBlock.vue] - 333', 'padding:2px;border-radius:4px;background:lightcoral;color:#fff', e)
    }
  }

  const exportGoodsExcel = async () => {
    const params: any = {
      ids: selectedRowKeys.value,
      prodChnl: searchObj.value.prodChnl || '',
      dbtId: searchObj.value.dbtId,
    }
    if (!params.dbtId) return
    exportLoading.value = true
    const type = ['xiaomi', 'honor'].includes(searchObj.value.prodChnl as string) ? 'xlsx' : 'csv'
    handleExportFile(await exportGoodsCsv(params), `${appName.value}_商品表`, type)
    exportLoading.value = false
    reportEvent({
      eventName: 'element_use',
      properties: {
        ui_name: '商品审核',
        element_name: '导出CSV',
      },
    })
  }

  const setProductName = (name: string) => {
    appName.value = name
  }

  const isProdStatus = computed(
    () =>
      (searchObj.value.publishStatuses?.includes(0) && searchObj.value.publishStatuses?.includes(20)) ||
      searchObj.value.approvalStatuses?.includes(STATUS_DELETED),
  )

  const isShowDelButton = computed(
    () => searchObj.value.publishStatuses?.includes(10) && searchObj.value.publishStatuses?.length === 1,
  )

  const pendingReviewNum = computed(() => {
    return (
      dataSource.value.filter((item: any) => selectedRowKeys.value.includes(item.id) && item.approvalStatus === 10)
        ?.length || 0
    )
  })

  const toBeRejectNum = computed(() => {
    return (
      dataSource.value.filter(
        (item: any) => selectedRowKeys.value.includes(item.id) && !(item.approvalStatus === 20 || !!item.prodStatus),
      )?.length || 0
    )
  })

  const toBeReleasedNum = computed(() => {
    return (
      dataSource.value.filter(
        (item: any) =>
          selectedRowKeys.value.includes(item.id) && item.approvalStatus === 30 && item.publishStatus !== 10,
      )?.length || 0
    )
  })

  const calculateTableHeight = () => {
    const windowHeight = window.innerHeight
    const otherHeight = 54 + 40 + 64 + 32 + 42 + 56 + 20 + 160
    tableHeight.value = windowHeight - otherHeight
  }

  const deleteChinaSaleArea = () => {
    // 过滤掉已经包含 CHN 的数据
    const filteredIds = dataSource.value
      .filter((item) => {
        if (!selectedRowKeys.value.includes(item.id)) return false
        try {
          const bizInfo = item.bizInfo || '{}'
          const removedAreaCodes = bizInfo.removedAreaCodes || []
          return !removedAreaCodes.includes('CHN')
        } catch (e) {
          return true
        }
      })
      .map((item) => item.id)

    if (filteredIds.length === 0) {
      message.info('所选商品已删除中国销售区域，无需重复操作')
      return
    }

    Modal.confirm({
      title: '删除中国销售地区',
      content: '确认后所有已创建商品将删除销售地区：中国',
      centered: true,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await cancelSaleArea({
            areaCodes: ['CHN'],
            ids: filteredIds,
            dbtId: searchObj.value?.dbtId,
            prodChnl: searchObj.value?.prodChnl || '',
          })
          if (res.code === OK) {
            message.success('删除中国销售区域成功')
            getData()
          } else {
            message.error(res.msg || '删除中国销售区域失败')
          }
        } catch (error) {
          console.error('删除中国销售区域失败:', error)
          message.error('删除中国销售区域失败')
        }
      },
    })
  }

  // SSE长链接
  const syncProductSSE = async () => {
    // 清空信息
    syncProductSSEMessage.value = []
    sseMessageModal.value = true
    sseMessageLoading.value = true

    if (!selectedRowKeys.value.length) return message.error('请选择商品')

    loading.value = true
    syncProductSSEMessage.value.push('开始同步商品状态')

    // 准备POST请求数据
    const postData = {
      prodChnl: searchObj.value.prodChnl || '',
      productList: selectedRowKeys.value.map((item: any) => ({ id: item })),
    }

    try {
      // 发起POST请求
      const preUrl =
        process.env.VUE_APP_ISJOINT === 'true' && process.env.NODE_ENV === 'development'
          ? ''
          : process.env.VUE_APP_DOMAIN
      // 从浏览器获取当前所有cookie
      const response = await fetch(
        `${preUrl}${preUrl?.endsWith('/') ? '' : '/'}${process.env.VUE_APP_SERVENAME}/product/publishProductSSE`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(postData),
        },
      )

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }

      // 获取响应的数据流
      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('无法获取响应数据流')
      }

      // 用于解析不完整的行
      let partialLine = ''

      // 读取并处理数据流
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        // 将二进制数据转换为文本
        const text = new TextDecoder().decode(value)
        const lines = (partialLine + text).split('\n')

        // 保存最后一行（可能不完整）
        partialLine = lines.pop() || ''

        // // 处理每一行数据
        for (const line of lines) {
          if (line.trim() === '') continue

          // SSE格式通常是 "data: {json数据}"
          if (line.startsWith('event:')) {
            try {
              const jsonData = line.slice(6).trim()
              syncProductSSEMessage.value.push(jsonData)

              // 根据返回数据处理不同状态
              if (jsonData === 'complete') {
                sseMessageLoading.value = false
              }
            } catch (e) {
              console.error('解析SSE消息失败:', e)
              syncProductSSEMessage.value.push(`解析SSE消息失败: ${e}`)
            }
          } else if (line.startsWith('data:')) {
            // 更新同一条消息，而不是创建新消息
            syncProductSSEMessage.value.push(line.slice(5).trim())
          }
        }
      }
    } catch (error) {
      console.error('SSE连接错误:', error)
      syncProductSSEMessage.value.push(`同步状态连接出错，请稍后重试，错误原因：${error}`)
    } finally {
      loading.value = false
      selectedRowKeys.value = []
      pageIndex.value = 1
      await getData()
      emits('updateStatistics')
      loading.value = false
      reportEvent({
        eventName: 'element_use',
        properties: {
          ui_name: '商品审核',
          element_name: '同步商品',
        },
      })
    }
  }

  const handleSSEMessageModalOk = () => {
    if (sseMessageLoading.value) {
      Modal.confirm({
        title: '提示',
        content: '关闭后无法查看同步最终结果，自行刷新页面查看',
        centered: true,
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          syncProductSSEMessage.value = []
          sseMessageModal.value = false
        },
      })
    } else {
      syncProductSSEMessage.value = []
      sseMessageModal.value = false
    }
  }

  const handlerDelModal = () => {
    Modal.confirm({
      title: '提示',
      content: `是否确认删除 ${canDelList.value.length} 条商品?`,
      centered: true,
      async onOk() {
        return new Promise(async (resolve, reject) => {
          try {
            const ids = canDelList.value.map((item) => item.id)
            const { code, msg } = await deletedProducts({
              ids,
              prodChnl: searchObj.value.prodChnl || '',
              dbtId: searchObj.value.dbtId,
            })

            if (code === OK) {
              message.success(msg)
              emits('updateStatistics')
              getData()
              resolve(true) // 关闭弹窗
            } else {
              reject(false) // 不关闭
            }
          } catch (e) {
            console.log(
              '%c[JudgeFilterBlock.vue] - 67',
              'padding:2px;border-radius:4px;background:lightcoral;color:#fff',
              e,
            )
            reject(e) // 不关闭，方便重试
          }
        })
      },
    })
  }

  onMounted(() => {
    calculateTableHeight()
    window.addEventListener('resize', calculateTableHeight)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', calculateTableHeight)
  })

  return {
    userInfo,
    dataSource,
    selectedRowKeys,
    pageIndex,
    pageSize,
    pageTotal,
    tableRef,
    tableHeight,
    editItem,
    loading,
    passLoading,
    rejectVisible,
    isEditModal,
    appName,
    exportLoading,
    isProdStatus,
    pendingReviewNum,
    toBeRejectNum,
    toBeReleasedNum,
    searchObj,
    syncProductSSEMessage,
    sseMessageModal,
    sseMessageLoading,
    isShowDelButton,
    canDelList,
    delModalVisible,
    getData,
    searchData,
    openDetail,
    batchPass,
    batchReject,
    onSelectChange,
    batchPublishProduct,
    openRejectModal,
    productPass,
    submitReject,
    exportGoodsExcel,
    setProductName,
    deleteChinaSaleArea,
    handleSSEMessageModalOk,
    handlerDelModal,
  }
}
